import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Users,
  Radio,
  Calendar,
  Clock,
  Video,
  LogIn,
  ExternalLink,
  User,
  Crown
} from 'lucide-react';

const TownHallPage = () => {
  const { user, token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (activeSession) {
      fetchMessages();
      // Poll for new messages every 3 seconds
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [activeSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/townhall/sessions`);
      setSessions(response.data);
      
      // Auto-select active session
      const active = response.data.find(s => s.is_active);
      if (active) {
        setActiveSession(active);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!activeSession) return;
    
    try {
      const response = await axios.get(`${API}/townhall/sessions/${activeSession.id}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to participate');
      return;
    }
    
    if (!newMessage.trim() || !activeSession) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/townhall/sessions/${activeSession.id}/messages`, 
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getEmbedUrl = (url, platform) => {
    if (!url) return null;
    
    if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId[1]}?autoplay=1`;
      }
    }
    
    if (platform === 'facebook' || url.includes('facebook.com')) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true`;
    }
    
    return null;
  };

  const activeSessions = sessions.filter(s => s.is_active);
  const upcomingSessions = sessions.filter(s => !s.is_active && s.scheduled_time && new Date(s.scheduled_time) > new Date());

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-plp-blue to-plp-blue-light text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <MessageSquare className="w-12 h-12 text-plp-gold" />
            <h1 className="font-heading text-4xl font-bold">Town Hall</h1>
          </div>
          <p className="text-xl text-blue-100 max-w-2xl">
            Join live conversations with Robyn Lyons and your community. Share your thoughts, ask questions, and be part of the discussion.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* No Sessions State */}
        {sessions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Town Hall Sessions</h3>
              <p className="text-slate-500 mt-1">Check back later for upcoming community discussions</p>
            </CardContent>
          </Card>
        ) : activeSession ? (
          /* Active Session View */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Stream / Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* Session Header */}
              <Card className="border-2 border-emerald-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-emerald-500 text-white animate-pulse">
                          <Radio className="w-3 h-3 mr-1" /> LIVE NOW
                        </Badge>
                      </div>
                      <h2 className="font-heading text-xl font-bold text-slate-800">{activeSession.title}</h2>
                      {activeSession.description && (
                        <p className="text-slate-600 mt-1">{activeSession.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 flex items-center gap-1 justify-end">
                        <Users className="w-4 h-4" />
                        {activeSession.participant_count || 0} participants
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Video Stream (if available) */}
              {activeSession.stream_url && (
                <Card>
                  <CardContent className="p-0">
                    {getEmbedUrl(activeSession.stream_url, activeSession.stream_platform) ? (
                      <div className="aspect-video">
                        <iframe
                          src={getEmbedUrl(activeSession.stream_url, activeSession.stream_platform)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="p-6 text-center">
                        <Video className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                        <a 
                          href={activeSession.stream_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-plp-blue hover:underline flex items-center gap-2 justify-center"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Watch Live Stream
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Chat Section */}
            <div className="lg:col-span-1">
              <Card className="h-[600px] flex flex-col">
                <div className="p-4 border-b bg-plp-blue text-white rounded-t-lg">
                  <h3 className="font-heading font-bold flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Live Chat
                  </h3>
                  <p className="text-sm text-blue-100">{messages.length} messages</p>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <p className="text-center text-slate-400 py-8">
                        No messages yet. Be the first to say something!
                      </p>
                    ) : (
                      messages.map((msg) => (
                        <div key={msg.id} className={`flex gap-3 ${msg.is_admin ? 'bg-plp-gold/10 p-3 rounded-lg -mx-2' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.is_admin ? 'bg-plp-gold text-plp-blue' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {msg.is_admin ? <Crown className="w-4 h-4" /> : <User className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium text-sm ${msg.is_admin ? 'text-plp-blue' : 'text-slate-700'}`}>
                                {msg.user_name || 'Anonymous'}
                              </span>
                              {msg.is_admin && (
                                <Badge className="bg-plp-gold text-plp-blue text-xs">Robyn</Badge>
                              )}
                              <span className="text-xs text-slate-400">
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1 break-words">{msg.content}</p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t">
                  {!user ? (
                    <Link to="/auth">
                      <Button className="w-full bg-plp-gold hover:bg-plp-gold/90 text-plp-blue">
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In to Chat
                      </Button>
                    </Link>
                  ) : (
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        disabled={sending}
                        className="flex-1"
                        data-testid="chat-message-input"
                      />
                      <Button 
                        type="submit" 
                        disabled={sending || !newMessage.trim()}
                        className="bg-plp-blue hover:bg-plp-blue/90"
                        data-testid="send-message-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  )}
                </div>
              </Card>
            </div>
          </div>
        ) : (
          /* No Active Session - Show Upcoming */
          <div className="space-y-8">
            {upcomingSessions.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-plp-gold" />
                  Upcoming Town Hall Sessions
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcomingSessions.map((session) => (
                    <Card key={session.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-plp-blue">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(session.scheduled_time).toLocaleDateString([], { 
                              weekday: 'short',
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Badge>
                        </div>
                        <h3 className="font-heading font-bold text-lg text-slate-800">{session.title}</h3>
                        {session.description && (
                          <p className="text-slate-600 mt-2 text-sm line-clamp-2">{session.description}</p>
                        )}
                        {session.stream_url && (
                          <p className="text-sm text-plp-blue mt-3 flex items-center gap-1">
                            <Video className="w-4 h-4" />
                            Live stream included
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeSessions.length === 0 && upcomingSessions.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-600">No Sessions Scheduled</h3>
                  <p className="text-slate-500 mt-1">Check back later for upcoming Town Hall discussions</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TownHallPage;
